import { hasStakeInHand } from './hasStakeInHand';
import { parseOffer } from './multiRunPhase';
import { runFromSeatAction } from './voteFromAction';

/**
 * How the multi-run vote PRESENTS at the table (EN-10856): the felt state, the
 * felt-level text and the seat roles all read this one module, so they cannot
 * disagree about what is happening. Who is asked, and what they may choose,
 * stays in multiRunPhase.ts; this module only describes the table.
 *
 * Read from `runs` on every frame, with no state of its own:
 *   - `v…` with no offer        → choosing (the initiator is deciding)
 *   - `v…` with an offer of n   → offered  (everyone else answers n)
 *   - `<n>,<stage>,<run>`       → running  (the agreed boards are being dealt)
 *   - `-2` with vote tokens     → once     (1X, a decline or a timeout)
 *   - anything else             → idle
 *
 * `-2` is also what a hand with no vote at all carries, so "once" needs the
 * seats' vote tokens to still be on the felt — they are, until the next street
 * is dealt, which is exactly as long as the bubbles that say the same thing.
 * One case reads idle instead: an all-in seat leaving mid-offer ends the vote
 * with no token anywhere. Showing it needs a reducer-side memory of the offer.
 */
export type MultiRunVotePhase = 'idle' | 'choosing' | 'offered' | 'once' | 'running';

export type MultiRunVoteState = {
  phase: MultiRunVotePhase;
  /** The offered count while offered, the agreed count while running; else 0. */
  offer: number;
};

const IDLE: MultiRunVoteState = { phase: 'idle', offer: 0 };

type Game = Record<string, unknown> | undefined;

const hasVoteTokenOnFelt = (game: Game): boolean => {
  for (let seat = 1; seat <= 10; seat += 1) {
    if (runFromSeatAction((game as any)?.[`p${seat}action`]) > 0) return true;
  }
  return false;
};

export const readVotePhase = (game: Game): MultiRunVoteState => {
  const runs = String((game as any)?.runs ?? '');

  if (runs.includes('v')) {
    const offer = parseOffer(runs);
    return offer === 0 ? { phase: 'choosing', offer: 0 } : { phase: 'offered', offer };
  }

  const runOut = runs.split(',');
  if (runOut.length === 3) {
    const total = parseInt(runOut[0], 10);
    return total >= 2 && total <= 3 ? { phase: 'running', offer: total } : IDLE;
  }

  if (runs === '-2' && hasVoteTokenOnFelt(game)) return { phase: 'once', offer: 0 };

  return IDLE;
};

/**
 * The phase as one primitive ('offered:2'), for `useTableSelector`: a selector
 * that returned the object would re-render its component on every frame.
 */
export const votePhaseKey = (game: Game): string => {
  const { phase, offer } = readVotePhase(game);
  return offer > 0 ? `${phase}:${offer}` : phase;
};

export const parseVotePhaseKey = (key: string): MultiRunVoteState => {
  const [phase, count] = key.split(':');
  const offer = count === undefined ? 0 : parseInt(count, 10);

  switch (phase) {
    case 'choosing':
    case 'once':
      return offer === 0 ? { phase, offer } : IDLE;
    case 'offered':
    case 'running':
      return offer === 2 || offer === 3 ? { phase, offer } : IDLE;
    default:
      return IDLE;
  }
};

/** A vote window is open: the table is in its multi-run state. */
export const isVoteOpen = ({ phase }: MultiRunVoteState): boolean =>
  phase === 'choosing' || phase === 'offered';

/**
 * Is this seat still playing for the pot? Stake in the hand, and not folded
 * or waiting for the button. Unlike the "is this seat asked" rule, a seat's
 * status is not consulted: an all-in seat that has since left the table is
 * never asked, but its chips are still in the pot, so it stays in the light.
 */
export const isContestingPot = (game: Game, seatId: number): boolean => {
  const marker = String((game as any)?.[`p${seatId}bet`] ?? '').substring(0, 1);
  if (marker === 'F' || marker === 'J') return false;
  return hasStakeInHand(game, seatId);
};

/**
 * The seat's part in the felt state while a vote is open: 'live' (spotlit,
 * still playing for the pot) or 'out' (dimmed). Undefined otherwise, so the
 * attribute is simply absent outside a vote. Callers pass occupied seats only.
 */
export type SeatVoteRole = 'live' | 'out';

export const seatVoteRole = (game: Game, seatId: number): SeatVoteRole | undefined => {
  if (!isVoteOpen(readVotePhase(game))) return undefined;
  return isContestingPot(game, seatId) ? 'live' : 'out';
};
